(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initHeader = (scope = document) => {
    const closeLanguagePickers = (except = null) => {
      document.querySelectorAll('[data-language-picker].is-open').forEach((picker) => {
        if (picker === except) return;
        picker.classList.remove('is-open');
        picker.querySelector('[data-language-toggle]')?.setAttribute('aria-expanded', 'false');
        picker.querySelector('[data-language-menu]')?.setAttribute('aria-hidden', 'true');
      });
    };

    scope.querySelectorAll('[data-language-picker]').forEach((picker) => {
      if (picker.dataset.initialized === 'true') return;
      const toggle = picker.querySelector('[data-language-toggle]');
      const panel = picker.querySelector('[data-language-menu]');
      if (!toggle || !panel) return;
      picker.dataset.initialized = 'true';

      const closePicker = () => {
        picker.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
      };

      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        closeLanguagePickers(picker);
        picker.closest('[data-site-header]')?.classList.remove('is-hidden');
        picker.classList.toggle('is-open', !open);
        toggle.setAttribute('aria-expanded', String(!open));
        panel.setAttribute('aria-hidden', String(open));
      });
      document.addEventListener('click', (event) => {
        if (!picker.contains(event.target)) closePicker();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closePicker();
      });
    });

    scope.querySelectorAll('[data-menu-toggle]').forEach((menuButton) => {
      if (menuButton.dataset.initialized === 'true') return;
      const menuId = menuButton.getAttribute('aria-controls');
      const mobileMenu = document.getElementById(menuId);
      if (!mobileMenu) return;
      const header = menuButton.closest('[data-site-header]');

      menuButton.dataset.initialized = 'true';
      const closeMenu = () => {
        menuButton.setAttribute('aria-expanded', 'false');
        mobileMenu.classList.remove('is-open');
        document.body.classList.remove('menu-open');
      };

      menuButton.addEventListener('click', () => {
        const open = menuButton.getAttribute('aria-expanded') === 'true';
        closeLanguagePickers();
        header?.classList.remove('is-hidden');
        menuButton.setAttribute('aria-expanded', String(!open));
        mobileMenu.classList.toggle('is-open', !open);
        document.body.classList.toggle('menu-open', !open);
      });
      mobileMenu.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeMenu();
      });
    });

    scope.querySelectorAll('[data-site-header]').forEach((header) => {
      if (header.dataset.scrollInitialized === 'true') return;
      header.dataset.scrollInitialized = 'true';
      let lastScrollY = window.scrollY;
      let hideDistance = 0;
      let revealDistance = 0;
      let ticking = false;

      const updateHeader = () => {
        const currentScrollY = Math.max(0, window.scrollY);
        const delta = currentScrollY - lastScrollY;
        const interactionOpen = document.body.classList.contains('menu-open') || header.querySelector('[data-language-picker].is-open');

        if (interactionOpen || currentScrollY < 28) {
          header.classList.remove('is-hidden');
          hideDistance = 0;
          revealDistance = 0;
        } else if (delta > 0) {
          hideDistance += delta;
          revealDistance = 0;
          if (hideDistance > 34 && currentScrollY > 120) header.classList.add('is-hidden');
        } else if (delta < 0) {
          revealDistance += Math.abs(delta);
          hideDistance = 0;
          if (revealDistance > 18) header.classList.remove('is-hidden');
        }

        lastScrollY = currentScrollY;
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateHeader);
      }, { passive: true });
    });

  };

  const initNewsletterForms = (scope = document) => {
    scope.querySelectorAll('[data-newsletter-container]').forEach((container) => {
      if (container.dataset.initialized === 'true') return;
      const form = container.querySelector('form');
      const email = form?.querySelector('input[type="email"][name="contact[email]"]');
      const submit = container.querySelector('[data-newsletter-submit]');
      const status = container.querySelector('.newsletter-form__status');
      const celebration = container.querySelector('[data-newsletter-celebration]');
      const celebrationText = container.querySelector('[data-newsletter-celebration-text]');
      const responseFrame = container.querySelector('[data-newsletter-target]');
      const successMessage = container.querySelector('[data-newsletter-message-success]')?.textContent.trim() || 'Thanks for subscribing!';
      const errorMessage = container.querySelector('[data-newsletter-message-error]')?.textContent.trim() || 'Please try again.';
      const invalidMessage = container.querySelector('[data-newsletter-message-invalid]')?.textContent.trim() || 'Enter a valid email address.';
      if (!form || !email || !submit || !celebration || !responseFrame) return;
      container.dataset.initialized = 'true';
      form.target = responseFrame.name;
      let feedbackTimer = null;
      let responseTimer = null;
      let pending = false;

      const hideFeedback = () => {
        celebration.classList.remove('is-visible');
        celebration.setAttribute('aria-hidden', 'true');
      };

      const showFeedback = (type, message) => {
        window.clearTimeout(feedbackTimer);
        celebration.classList.remove('is-visible', 'is-error');
        celebration.classList.toggle('is-error', type === 'error');
        if (celebrationText) celebrationText.textContent = message;
        void celebration.offsetWidth;
        celebration.classList.add('is-visible');
        celebration.setAttribute('aria-hidden', 'false');
        feedbackTimer = window.setTimeout(hideFeedback, type === 'error' ? 5000 : 3600);
      };

      const finish = (success, message) => {
        pending = false;
        window.clearTimeout(responseTimer);
        submit.removeAttribute('aria-busy');
        submit.disabled = false;
        if (success) {
          form.reset();
          email.setCustomValidity('');
          if (status) status.textContent = '';
          showFeedback('success', successMessage);
          return;
        }
        if (status) {
          status.textContent = message || errorMessage;
          status.classList.add('form-status--error');
        }
        showFeedback('error', message || errorMessage);
      };

      responseFrame.addEventListener('load', () => {
        if (!pending) return;
        try {
          const responseUrl = responseFrame.contentWindow.location.href;
          const responseDocument = responseFrame.contentDocument;
          const serverSuccess = responseDocument.querySelector('.newsletter-form .form-status--success');
          const serverError = responseDocument.querySelector('.newsletter-form .form-status--error, .newsletter-form .errors');
          const blocked = /challenge|captcha/i.test(responseUrl) || responseDocument.querySelector('form[action*="challenge"], [data-shopify-captcha]');
          const explicitSuccess = /(?:customer|contact)_posted=true/i.test(responseUrl) || Boolean(serverSuccess);
          const accepted = explicitSuccess || (!blocked && !serverError && Boolean(responseDocument.body));
          finish(accepted, serverError?.textContent.trim() || errorMessage);
        } catch (error) {
          finish(false, errorMessage);
        }
      });

      email.addEventListener('input', () => {
        email.setCustomValidity('');
        if (status) {
          status.textContent = '';
          status.classList.remove('form-status--error');
        }
      });

      email.addEventListener('focus', () => {
        if (window.matchMedia('(max-width: 640px)').matches) {
          window.setTimeout(() => email.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center', inline: 'nearest' }), 340);
        }
      });

      form.addEventListener('submit', (event) => {
        const normalizedEmail = email.value.trim();
        email.value = normalizedEmail;
        const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u.test(normalizedEmail);
        if (!validEmail) {
          event.preventDefault();
          email.setCustomValidity(invalidMessage);
          email.reportValidity();
          if (status) {
            status.textContent = invalidMessage;
            status.classList.add('form-status--error');
          }
          showFeedback('error', invalidMessage);
          return;
        }

        email.setCustomValidity('');
        pending = true;
        email.blur();
        if (status) {
          status.textContent = '';
          status.classList.remove('form-status--error');
        }
        submit.setAttribute('aria-busy', 'true');
        submit.disabled = true;
        window.clearTimeout(responseTimer);
        responseTimer = window.setTimeout(() => {
          if (pending) finish(false, errorMessage);
        }, 15000);
      });
    });
  };

  const initBenefitsCarousel = (scope = document) => {
    scope.querySelectorAll('[data-benefits-carousel]').forEach((carousel) => {
      if (carousel.dataset.initialized === 'true') return;
      const track = carousel.querySelector('[data-benefits-track]');
      const cards = Array.from(carousel.querySelectorAll('[data-benefit-card]'));
      const previous = carousel.querySelector('[data-benefits-previous]');
      const next = carousel.querySelector('[data-benefits-next]');
      const counter = carousel.querySelector('[data-benefits-current]');
      if (!track || !cards.length) return;
      carousel.dataset.initialized = 'true';
      let activeIndex = 0;
      let frameId = null;

      const setActive = (index) => {
        activeIndex = Math.max(0, Math.min(cards.length - 1, index));
        cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === activeIndex));
        if (counter) counter.textContent = String(activeIndex + 1);
        if (previous) previous.disabled = activeIndex === 0;
        if (next) next.disabled = activeIndex === cards.length - 1;
      };

      const findClosestCard = () => {
        const center = track.getBoundingClientRect().left + track.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        cards.forEach((card, index) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        setActive(closestIndex);
      };

      const goTo = (index) => {
        const targetIndex = Math.max(0, Math.min(cards.length - 1, index));
        cards[targetIndex].scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        setActive(targetIndex);
      };

      previous?.addEventListener('click', () => goTo(activeIndex - 1));
      next?.addEventListener('click', () => goTo(activeIndex + 1));
      track.addEventListener('scroll', () => {
        if (frameId) window.cancelAnimationFrame(frameId);
        frameId = window.requestAnimationFrame(findClosestCard);
      }, { passive: true });
      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(activeIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(activeIndex + 1);
        }
      });
      setActive(0);
    });
  };

  const initMobileComparison = (scope = document) => {
    scope.querySelectorAll('[data-mobile-comparison]').forEach((comparison) => {
      if (comparison.dataset.initialized === 'true') return;
      const tabs = Array.from(comparison.querySelectorAll('[data-comparison-metric]'));
      const viewport = comparison.querySelector('[data-comparison-viewport]');
      const track = comparison.querySelector('[data-comparison-track]');
      const originals = Array.from(comparison.querySelectorAll('[data-comparison-card]'));
      const previous = comparison.querySelector('[data-comparison-previous]');
      const next = comparison.querySelector('[data-comparison-next]');
      const dots = Array.from(comparison.querySelectorAll('[data-comparison-dot]'));
      const announcer = comparison.querySelector('[data-comparison-announcer]');
      if (!tabs.length || !viewport || !track || !originals.length) return;
      comparison.dataset.initialized = 'true';
      let activeIndex = Math.max(0, tabs.findIndex((tab) => tab.classList.contains('is-active')));
      let physicalIndex = originals.length > 1 ? activeIndex + 1 : activeIndex;
      let isAnimating = false;
      let pointerState = null;
      let cards = originals;

      const prepareClone = (card) => {
        const clone = card.cloneNode(true);
        clone.dataset.carouselClone = 'true';
        clone.removeAttribute('id');
        clone.removeAttribute('aria-labelledby');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('img').forEach((image) => image.classList.add('is-loaded'));
        return clone;
      };

      if (originals.length > 1) {
        track.prepend(prepareClone(originals[originals.length - 1]));
        track.append(prepareClone(originals[0]));
        cards = Array.from(track.querySelectorAll('[data-comparison-card]'));
      }

      const cardWidth = () => viewport.getBoundingClientRect().width;
      const transformTrack = (index, offset = 0, animate = true) => {
        track.style.transition = animate && !reducedMotion.matches ? '' : 'none';
        track.style.transform = `translate3d(${(-index * cardWidth()) + offset}px,0,0)`;
      };

      const setPhysicalActive = (index) => {
        cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === index));
      };

      const syncInterface = (nextIndex, direction = 1, scrollTab = true, announce = true) => {
        activeIndex = (nextIndex + originals.length) % originals.length;
        const normalizedDirection = direction < 0 ? -1 : 1;
        comparison.style.setProperty('--swipe-start', `${normalizedDirection * -3}deg`);
        comparison.style.setProperty('--swipe-over', `${normalizedDirection * .7}deg`);

        tabs.forEach((tab, index) => {
          const selected = index === activeIndex;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        dots.forEach((dot, index) => {
          const selected = index === activeIndex;
          dot.classList.toggle('is-active', selected);
          dot.setAttribute('aria-current', String(selected));
        });
        originals.forEach((card, index) => card.setAttribute('aria-hidden', String(index !== activeIndex)));

        if (scrollTab) {
          tabs[activeIndex].scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        }
        if (announce && announcer) announcer.textContent = tabs[activeIndex].textContent.trim();
      };

      const finishAtRealCard = () => {
        if (originals.length <= 1) return;
        if (physicalIndex === 0) physicalIndex = originals.length;
        else if (physicalIndex === originals.length + 1) physicalIndex = 1;
        else return;
        comparison.classList.add('is-loop-snap');
        transformTrack(physicalIndex, 0, false);
        setPhysicalActive(physicalIndex);
        track.getBoundingClientRect();
        track.style.transition = '';
        window.requestAnimationFrame(() => comparison.classList.remove('is-loop-snap'));
      };

      const goTo = (nextIndex, direction = null, options = {}) => {
        if (isAnimating && !reducedMotion.matches) return;
        const normalized = (nextIndex + originals.length) % originals.length;
        if (normalized === activeIndex && physicalIndex === (originals.length > 1 ? normalized + 1 : normalized)) {
          syncInterface(normalized, direction || 1, options.scrollTab !== false, false);
          return;
        }
        let travelDirection = direction;
        if (!travelDirection) {
          const delta = normalized - activeIndex;
          travelDirection = delta === 0 ? 1 : Math.sign(delta);
        }

        let targetPhysical = originals.length > 1 ? normalized + 1 : normalized;
        if (originals.length > 1 && travelDirection > 0 && activeIndex === originals.length - 1 && normalized === 0) {
          targetPhysical = originals.length + 1;
        } else if (originals.length > 1 && travelDirection < 0 && activeIndex === 0 && normalized === originals.length - 1) {
          targetPhysical = 0;
        }

        physicalIndex = targetPhysical;
        syncInterface(normalized, travelDirection, options.scrollTab !== false, options.announce !== false);
        setPhysicalActive(targetPhysical);
        isAnimating = !reducedMotion.matches && originals.length > 1;
        transformTrack(targetPhysical, 0, true);

        if (!isAnimating) {
          finishAtRealCard();
          isAnimating = false;
        }
      };

      const step = (direction) => goTo(activeIndex + direction, direction);

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => goTo(index, null));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') goTo(0, -1);
          else if (event.key === 'End') goTo(tabs.length - 1, 1);
          else step(event.key === 'ArrowRight' ? 1 : -1);
          tabs[(event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (activeIndex + tabs.length) % tabs.length)].focus({ preventScroll: true });
        });
      });

      dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));
      previous?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));

      track.addEventListener('transitionend', (event) => {
        if (event.propertyName !== 'transform') return;
        finishAtRealCard();
        isAnimating = false;
      });

      viewport.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        step(event.key === 'ArrowRight' ? 1 : -1);
      });

      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerState = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          time: performance.now(),
          lastX: event.clientX,
          lastTime: performance.now(),
          velocity: 0,
          horizontal: false
        };
        viewport.setPointerCapture?.(event.pointerId);
      });

      viewport.addEventListener('pointermove', (event) => {
        if (!pointerState || pointerState.id !== event.pointerId || isAnimating) return;
        const deltaX = event.clientX - pointerState.x;
        const deltaY = event.clientY - pointerState.y;
        if (!pointerState.horizontal && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
          pointerState.horizontal = true;
          viewport.classList.add('is-dragging');
        }
        if (!pointerState.horizontal) return;
        event.preventDefault();
        const now = performance.now();
        const elapsed = Math.max(1, now - pointerState.lastTime);
        pointerState.velocity = (event.clientX - pointerState.lastX) / elapsed;
        pointerState.lastX = event.clientX;
        pointerState.lastTime = now;
        const progress = Math.max(-1, Math.min(1, deltaX / Math.max(1, cardWidth())));
        comparison.style.setProperty('--drag-tilt', `${progress * -2.6}deg`);
        transformTrack(physicalIndex, deltaX * .94, false);
      });

      const releasePointer = (event, cancelled = false) => {
        if (!pointerState || pointerState.id !== event.pointerId) return;
        const deltaX = event.clientX - pointerState.x;
        const horizontal = pointerState.horizontal;
        const velocity = pointerState.velocity;
        pointerState = null;
        viewport.classList.remove('is-dragging');
        comparison.style.removeProperty('--drag-tilt');
        if (viewport.hasPointerCapture?.(event.pointerId)) viewport.releasePointerCapture(event.pointerId);

        if (!cancelled && horizontal && (Math.abs(deltaX) > cardWidth() * .16 || Math.abs(velocity) > .42)) {
          step(deltaX < 0 ? 1 : -1);
        } else {
          transformTrack(physicalIndex, 0, true);
        }
      };

      viewport.addEventListener('pointerup', (event) => releasePointer(event));
      viewport.addEventListener('pointercancel', (event) => releasePointer(event, true));

      const realign = () => transformTrack(physicalIndex, 0, false);
      if ('ResizeObserver' in window) new ResizeObserver(realign).observe(viewport);
      else window.addEventListener('resize', realign, { passive: true });

      syncInterface(activeIndex, 1, false, false);
      setPhysicalActive(physicalIndex);
      transformTrack(physicalIndex, 0, false);
      window.requestAnimationFrame(() => { track.style.transition = ''; });
    });
  };

  const initImageLoadingReveal = (scope = document) => {
    const images = Array.from(scope.querySelectorAll('img:not([data-image-reveal-initialized])'));
    images.forEach((image, index) => {
      image.dataset.imageRevealInitialized = 'true';
      image.classList.add('acorn-image-reveal');
      image.style.setProperty('--image-reveal-delay', `${Math.min(index % 6, 5) * 45}ms`);

      const reveal = () => {
        window.requestAnimationFrame(() => image.classList.add('is-loaded'));
      };

      if (image.complete) {
        reveal();
      } else {
        image.addEventListener('load', reveal, { once: true });
        image.addEventListener('error', reveal, { once: true });
      }
    });
  };

  const initAutoCenterScroll = (scope = document) => {
    scope.querySelectorAll('[data-auto-center-scroll]').forEach((track) => {
      if (track.dataset.autoCenterInitialized === 'true') return;
      track.dataset.autoCenterInitialized = 'true';
      const refresh = () => track.classList.toggle('is-overflowing', track.scrollWidth > track.clientWidth + 2);
      window.requestAnimationFrame(refresh);
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(refresh);
        observer.observe(track);
        Array.from(track.children).forEach((child) => observer.observe(child));
      } else {
        window.addEventListener('resize', refresh, { passive: true });
      }
    });
  };

  const initReveal = (scope = document) => {
    const revealItems = scope.querySelectorAll('.reveal:not([data-reveal-initialized])');
    if ('IntersectionObserver' in window && !reducedMotion.matches) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
      revealItems.forEach((item) => {
        item.dataset.revealInitialized = 'true';
        observer.observe(item);
      });
    } else {
      revealItems.forEach((item) => {
        item.dataset.revealInitialized = 'true';
        item.classList.add('is-visible');
      });
    }
  };

  const initBeforeAfter = (scope = document) => {
    scope.querySelectorAll('[data-before-after-section]').forEach((section) => {
      if (section.dataset.initialized === 'true') return;
      const comparison = section.querySelector('[data-before-after]');
      const range = section.querySelector('[data-before-after-range]');
      if (!comparison || !range) return;

      section.dataset.initialized = 'true';
      let frameId = null;
      let demoStarted = false;
      const storageKey = section.dataset.demoKey || 'acorn-storage-demo';
      const setPosition = (value) => {
        const normalized = Math.max(0, Math.min(100, Number(value)));
        comparison.style.setProperty('--position', `${normalized}%`);
        range.value = normalized;
      };
      const markPlayed = () => {
        try {
          window.localStorage.setItem(storageKey, 'played');
        } catch (error) {
          // Storage can be unavailable in privacy modes; the interaction still works.
        }
      };
      const cancelDemo = () => {
        if (frameId !== null) window.cancelAnimationFrame(frameId);
        frameId = null;
        if (demoStarted) markPlayed();
      };

      setPosition(range.value || 0);
      range.addEventListener('input', () => {
        cancelDemo();
        setPosition(range.value);
      });
      range.addEventListener('pointerdown', cancelDemo);
      range.addEventListener('keydown', cancelDemo);

      if (section.dataset.demoEnabled !== 'true' || reducedMotion.matches) return;
      let alreadyPlayed = false;
      try {
        alreadyPlayed = window.localStorage.getItem(storageKey) === 'played';
      } catch (error) {
        alreadyPlayed = false;
      }
      if (alreadyPlayed) return;

      const duration = Math.max(2, Number(section.dataset.demoDuration) || 5) * 1000;
      const playDemo = () => {
        if (demoStarted) return;
        demoStarted = true;
        const start = performance.now();
        const animate = (time) => {
          const progress = Math.min(1, (time - start) / duration);
          const eased = 0.5 - Math.cos(progress * Math.PI) / 2;
          const position = eased <= 0.5 ? eased * 200 : (1 - eased) * 200;
          setPosition(position);
          if (progress < 1) {
            frameId = window.requestAnimationFrame(animate);
          } else {
            frameId = null;
            setPosition(0);
            markPlayed();
          }
        };
        frameId = window.requestAnimationFrame(animate);
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            playDemo();
          }
        }, { threshold: 0.28 });
        observer.observe(section);
      } else {
        playDemo();
      }
    });
  };

  const initialize = (scope = document) => {
    initHeader(scope);
    initImageLoadingReveal(scope);
    initReveal(scope);
    initBeforeAfter(scope);
    initAutoCenterScroll(scope);
    initBenefitsCarousel(scope);
    initMobileComparison(scope);
    initNewsletterForms(scope);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
